import { createPortal } from "react-dom";
import type { UserBadge } from "@/hooks/useUserBadges";
import { badgeLabel, _badgeKindLabel } from "@/components/BadgeChip";

interface Props {
  badge: UserBadge | null;
  /** Optional preface text (used when surfacing the Collector tier via this modal). */
  tierName?: string | null;
  tierHint?: string | null;
  onClose: () => void;
}

function badgeDescription(badge: UserBadge): string {
  switch (badge.kind) {
    case "brand_authority":
      return `Awarded weekly to the FIG collector with the most PASS-verified scans of this brand. Held until someone else takes the lead.`;
    case "era_specialist":
      return `Awarded weekly to the collector with the most PASS-verified scans from this decade. Held until someone else takes the lead.`;
    case "diverse_collector":
      return `Awarded once for owning PASS-verified pieces across three or more decades. Permanent.`;
    case "fotw_winner":
      return `Awarded for the highest-rated verified piece of the week, by community vote. Permanent — your name in FIG's weekly history.`;
    case "fotw_candidate":
      return `Awarded for being shortlisted for Find of the Week. Marks a piece your peers ranked among the best of its week.`;
    case "founder":
      return `Awarded to the earliest FIG cohort — collectors who joined while the closet was still being built.`;
    case "verified_moderator":
      return `Awarded to community moderators who help keep fast fashion off FIG. Eligible after 10 PASS-verified posts on an active Seller / Pro Seller sub, then approved by the FIG team. Verified moderators get +5 free scans every month while active, and the badge appears on their profile and posts as a trust signal for buyers.`;
    default:
      return "";
  }
}

function formatAwarded(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * BadgeDetailModal — bottom-sheet on phone, centered overlay otherwise.
 * Renders either a `badge` or the Collector tier (when `tierName` is set
 * and `badge` is null).
 */
export function BadgeDetailModal({ badge, tierName, tierHint, onClose }: Props) {
  // Nothing to show — caller decided.
  if (!badge && !tierName) return null;

  const title = badge ? badgeLabel(badge) : (tierName ?? "");
  const subtitle = badge ? _badgeKindLabel(badge.kind) : "Collector tier";
  const meta = badge
    ? `Earned ${formatAwarded(badge.awarded_at)}${badge.expires_at ? ` · held until ${formatAwarded(badge.expires_at)}` : ""}`
    : (tierHint ?? "");
  const body = badge
    ? badgeDescription(badge)
    : "Your tier reflects how many PASS-verified pieces are in your closet. It updates live as your collection grows.";

  // Portal to document.body so the modal escapes MobileFrame's
  // `position: relative; zIndex: 5` stacking context. Without the portal,
  // the modal's zIndex is compared inside that context (5) and the
  // BottomDock at root-level zIndex 50 paints over the bottom of the
  // sheet — the "hidden under the nav bar" symptom.
  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Badge details"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "hsla(0,0%,0%,0.55)",
        zIndex: 100,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        // Backdrop drags don't reach MobileFrame's scroll container behind.
        touchAction: "none",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 430,
          background: "hsl(var(--cream))",
          borderTop: "1.5px solid hsl(var(--ink))",
          padding: "20px 20px calc(28px + env(safe-area-inset-bottom, 0px))",
          // Constrain height + scroll the body when content overflows.
          // overscroll-behavior contain stops chain-scroll from reaching
          // the page underneath. touch-action pan-y re-enables vertical
          // scroll inside (the backdrop sets touch-action: none).
          maxHeight: "85vh",
          overflowY: "auto",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase",
          color: "hsl(var(--ink-2))",
          marginBottom: 4,
        }}>
          {subtitle}
        </div>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontWeight: 800, fontSize: 24, lineHeight: 1.1,
          color: "hsl(var(--ink))",
          marginBottom: 6,
        }}>
          {title}
        </div>
        {meta && (
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10, letterSpacing: "0.1em",
            color: "hsl(var(--rust))",
            marginBottom: 12,
          }}>
            {meta}
          </div>
        )}
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 14, lineHeight: 1.5,
          color: "hsl(var(--ink))",
          marginBottom: 18,
        }}>
          {body}
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%",
            background: "transparent",
            border: "1px dashed hsl(var(--ink-2))",
            padding: "10px",
            cursor: "pointer",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
            color: "hsl(var(--ink-2))",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
